<template>
  <div
    v-if="show"
    class="reason-container bg-light-gray rounded p-3"
    :style="{ 'border-left-color': currentStatusTargetFormColor }"
  >
    <div class="row">
      <div class="col-12">
        <span class="title d-flex align-items-center">
          <div class="circle-icon d-flex mr-2">
            <span class="fa-stack fa-1x">
              <i class="fas fa-circle fa-stack-2x text-white" />
              <i
                :class="`fas ${currentStatusTargetFormIcon} fa-stack-1x`"
                :style="{ color: currentStatusTargetFormColor }"
              />
            </span>
          </div>
          <span v-text="title" />
        </span>
      </div>
    </div>

    <div class="col-12">
      <Component
        :is="`${currentStatusTargetForm}Form`"
        :current-status="currentStatus"
        :current-status-target="currentStatusTarget"
        :readonly="readonly"
        :approval-academic-year="approvalInfo.academicYear"
        :approval-determination="approvalInfo.determination"
        :academic-year-id="academicYearId"
        @submit="submit"
        @cancel="cancel"
      />
    </div>
  </div>
</template>

<script>
import CustomTextArea from 'CustomTextArea';
import {
  ApprovalForm,
  DisapprovalForm,
  ReturnForm,
  DenegadoForm,
} from './_forms';
import alert from '@/mixin/alert.mixin';
import { emitAsyncMixin } from '@/mixin/emit-async.mixin';
import { modelToPayload } from './_utils';
import { mapActions, mapGetters, mapState } from 'vuex';
import { statusManagementStore } from '@/components/pei/ubication-tech-assistance/_store';
import { tapRequestStatusesEnum } from '@/components/pei/ubication-tech-assistance/_types';
import { UTA_MANAGEMENT_STATUS_TARGET_CONFIG } from './_config';

export default {
  name: 'ChangeOfStatus',
  mixins: [alert, emitAsyncMixin],
  components: {
    CustomTextArea,
    ApprovalForm,
    DisapprovalForm,
    ReturnForm,
    DenegadoForm,
  },
  props: {
    tapRequestId: { type: [Number, String], required: true, default: null },
    academicYearId: { type: Number, required: true, default: null },
    tapRequestTransferServiceDetermination: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapState(statusManagementStore, [
      'currentStatus',
      'currentStatusTarget',
      'approvalInfo',
    ]),
    ...mapGetters(statusManagementStore, ['currentTabState']),
    currentStatusConfig() {
      if (this.readonly)
        return UTA_MANAGEMENT_STATUS_TARGET_CONFIG[this.currentStatus.id];
      if (!this.currentStatusTarget) return null;
      return UTA_MANAGEMENT_STATUS_TARGET_CONFIG[this.currentStatusTarget.id];
    },
    currentStatusTargetForm() {
      return this.currentStatusConfig?.form || 'Return';
    },
    currentStatusTargetFormTitle() {
      return this.currentStatusConfig?.formTitle || '';
    },
    currentStatusTargetFormIcon() {
      return this.currentStatusConfig?.formIcon || 'fa-undo-alt';
    },
    currentStatusTargetFormColor() {
      return this.currentStatusConfig?.formColor || '#FF7058';
    },
    show() {
      if (this.currentStatusTarget) return this.currentStatusTargetShowForm;
      return (
        this.currentTabState?.showStatusCard ||
        this.currentStatusTargetShowForm ||
        false
      );
    },
    readonly() {
      if (this.currentStatusTarget) return false;
      return this.currentTabState?.statusCardReadonly || false;
    },
    title() {
      return (
        this.currentStatusTargetFormTitle ||
        (this.readonly
          ? this.currentStatus?.name || ''
          : this.currentStatusTarget?.description || '')
      );
    },
    currentStatusTargetShowForm() {
      if (!this.currentStatusTarget) return false;
      const { needReason, needReasonType } = this.currentStatusTarget;
      return (
        needReason ||
        needReasonType ||
        this.currentStatusConfig?.showForm ||
        false
      );
    },
  },
  methods: {
    ...mapActions(statusManagementStore, [
      'setCurrentStatus',
      'setCurrentStatusTarget',
      'setStatusTargets',
      'setApprovalNotifiedInfo',
    ]),
    async submit(
      model = { currentStatusTargetId: this.currentStatusTarget.id }
    ) {
      if (this.readonly) return;
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea cambiar el estatus de ${this.currentStatus.name} a ${this.currentStatusTarget.name}?`,
        'Sí',
        'No'
      );
      if (confirmed) {
        let res = await this.$emitAsync('before-status-changed');
        if (!res) {
          if (!this.show) {
            this.cancel();
          }
          return;
        }
        try {
          const url = `/api/tapRequests/${this.tapRequestId}/changestatus`;
          const payload = modelToPayload(model);
          const { data } = await this.$ApiPut(url, payload);
          if (data) {
            const {
              statusId,
              statusName,
              statusReasons,
              reason,
              availableStates,
            } = data;

            this.setCurrentStatus({
              id: statusId,
              name: statusName,
              reason,
              reasonTypes: [...statusReasons],
            });
            this.setStatusTargets(availableStates);
            if (
              [
                tapRequestStatusesEnum.Aprobado,
                tapRequestStatusesEnum.Notificado,
              ].includes(statusId)
            ) {
              const { approvalAcademicYear, approvalDetermination } = data;
              this.setApprovalNotifiedInfo({
                academicYear: approvalAcademicYear,
                determination: approvalDetermination,
              });
            }

            this.ShowToast(
              'Proceso Exitoso',
              'La solicitud ha sido cambiada de estatus exitosamente.',
              'success'
            );
            this.$emit('after-status-changed', data);
          } else {
            this.$emit('after-status-changed', null);
          }
        } catch {
          this.$emit('after-status-changed', null);
        }
      }
      this.cancel();
    },
    cancel() {
      this.setCurrentStatusTarget(null);
    },
  },
  watch: {
    currentStatusTarget: {
      async handler(value, oldValue) {
        if (this.currentStatusTargetShowForm) return;
        if (value && value !== oldValue) {
          await this.submit();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.reason-container {
  border-left: 6px solid white;
  .circle-icon {
    box-shadow: 0px 3px 6px #00000029;
    width: 35px;
    border-radius: 35px;
  }
  .title {
    font: normal normal bold 16px/19px Lato;
    color: #3f5b60;
  }
}
textarea {
  min-height: 50px !important;
}
</style>
